// src/app/pages/expediente/expediente-digital/sections/paraclinicos/paraclinicos-page/components/historico-laboratorio/historico-laboratorio.component.ts

import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { LaboratorioService } from '@services/laboratorio/laboratorio.service';
import {
  HistoricoLaboratorio,
  ParametroHistorico,
} from '../../../../../../../../interfaces/laboratorio/historico-laboratorio.interface';

@Component({
  selector: 'app-historico-laboratorio',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './historico-laboratorio.component.html',
  styleUrls: ['./historico-laboratorio.component.scss'],
})
export class HistoricoLaboratorioComponent implements OnInit {
  @Input() pacienteId!: number;
  @Input() categoria?: string;
  @Input() useMockData: boolean = false; // Toggle para usar mock data

  // Datos
  historico: HistoricoLaboratorio | null = null;
  isLoading = false;
  error: string | null = null;

  constructor(private laboratorioService: LaboratorioService) {}

  async ngOnInit() {
    await this.cargarHistorico();
  }

  /**
   * Cargar histórico del backend
   */
  async cargarHistorico() {
    if (!this.pacienteId) {
      this.error = 'No se proporcionó ID de paciente';
      return;
    }

    this.isLoading = true;
    this.error = null;

    try {
      if (this.useMockData) {
        // Usar mock data (importar desde archivo)
        const { MOCK_HISTORICO_LABORATORIO } = await import(
          '../../../../../../../../mock-data/laboratorio/historico.mock'
        );
        this.historico = MOCK_HISTORICO_LABORATORIO;
        console.log('📊 Mock data cargado:', this.historico);
      } else {
        // Cargar desde API
        this.historico = await this.laboratorioService.getHistorico(this.pacienteId, this.categoria);
        console.log('📊 Histórico cargado desde API:', this.historico);
      }
    } catch (err: any) {
      console.error('Error cargando histórico:', err);
      this.error = err.message || 'Error al cargar el histórico';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Obtener valor para una fecha específica
   */
  getValorParaFecha(parametro: ParametroHistorico, fecha: string): any {
    const valor = parametro.valores.find((v) => v.fecha === fecha);
    return valor || null;
  }

  /**
   * Obtener clase CSS para celda según estado
   */
  getCeldaClass(parametro: ParametroHistorico, fecha: string): string {
    const valor = this.getValorParaFecha(parametro, fecha);

    if (!valor) return 'celda-vacia';

    if (valor.fueraRango) {
      return valor.nivel === 'bajo' ? 'celda-bajo' : 'celda-alto';
    }

    return 'celda-normal';
  }

  /**
   * Formatear fecha para display (DD/MM)
   */
  formatearFecha(fecha: string): string {
    const [year, month, day] = fecha.split('-');
    return `${day}/${month}`;
  }

  /**
   * Formatear fecha completa
   */
  formatearFechaCompleta(fecha: string): string {
    const [year, month, day] = fecha.split('-');
    const meses = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${day} ${meses[parseInt(month) - 1]} ${year}`;
  }

  /**
   * Obtener texto del valor
   */
  getValorTexto(valor: any): string {
    if (!valor) return '-';

    if (typeof valor.valor === 'number') {
      return valor.valor.toFixed(2);
    }

    return valor.valor?.toString() || '-';
  }

  /**
   * Recargar datos
   */
  async recargar() {
    await this.cargarHistorico();
  }

  /**
   * Agrupar parámetros por categoría
   */
  getParametrosPorCategoria(): { [categoria: string]: ParametroHistorico[] } {
    if (!this.historico) return {};

    const grupos: { [categoria: string]: ParametroHistorico[] } = {};

    this.historico.parametros.forEach((param) => {
      if (!grupos[param.categoria]) {
        grupos[param.categoria] = [];
      }
      grupos[param.categoria].push(param);
    });

    return grupos;
  }

  /**
   * Obtener keys de objeto
   */
  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }
}
